/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";

interface SaveReportModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (assignedMonth: string) => void;
  billingCycleStart?: string;
  billingCycleEnd?: string;
  isSaving?: boolean;
}

/**
 * Derives the available month options from the billing cycle date range.
 * Each month that overlaps with [start, end) is included as an option.
 */
const deriveMonthOptions = (
  start?: string,
  end?: string,
): { value: string; label: string }[] => {
  if (!start || !end) return [];

  const startDate = dayjs(start);
  const endDate = dayjs(end);

  if (!startDate.isValid() || !endDate.isValid()) return [];

  const options: { value: string; label: string }[] = [];
  let current = startDate.startOf("month");
  const lastMonth = endDate.startOf("month");

  while (current.isBefore(lastMonth) || current.isSame(lastMonth, "month")) {
    options.push({
      value: current.format("YYYY-MM-01"),
      label: current.format("MMMM YYYY"),
    });
    current = current.add(1, "month");
  }

  return options;
};

const SaveReportModal: React.FC<SaveReportModalProps> = ({
  open,
  onClose,
  onSave,
  billingCycleStart,
  billingCycleEnd,
  isSaving = false,
}) => {
  const [selectedMonth, setSelectedMonth] = useState<string>("");

  const monthOptions = useMemo(
    () => deriveMonthOptions(billingCycleStart, billingCycleEnd),
    [billingCycleStart, billingCycleEnd],
  );

  const handleSave = () => {
    if (selectedMonth) {
      onSave(selectedMonth);
    }
  };

  const handleClose = () => {
    setSelectedMonth("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          p: 4,
          borderRadius: "4px",
          boxShadow: "0px 4px 18px 0px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: "24px",
              lineHeight: 1.334,
              color: "text.primary",
            }}
          >
            Save Report
          </Typography>

          {/* Month Selector */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl variant="outlined" sx={{ width: 258 }}>
              <InputLabel id="assign-month-label">Assign to Month</InputLabel>
              <Select
                labelId="assign-month-label"
                id="assign-month-select"
                value={selectedMonth}
                label="Assign to Month"
                onChange={(e) => setSelectedMonth(e.target.value as string)}
              >
                {monthOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.43,
                letterSpacing: "0.17px",
                color: "text.secondary",
              }}
            >
              If there is a report for this month already, this will overwrite
              it.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!selectedMonth || isSaving}
            >
              Save Report
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SaveReportModal;
